import cn from 'classnames'
import { memo, useCallback } from 'react'

import { useSafetyTranslation } from 'cng-common/features/translations'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Input } from 'core-components/library/inputs'
import { Tooltip } from 'core-components/library/tooltip'

import { useFormField } from '../../hooks/useFormField'
import { ReadOnlyField } from '../readOnlyField'

import { IFormInputProps } from './models'

import styles from './FormInput.module.scss'

export const CustomInput = <T extends string | number>({
  field,
  formKey,
  label = '',
  defaultValue,
  className,
  endContentClassName,
  isDisabled,
  isRequired,
  isReadOnly,
  isVisible = true,
  onChange,
  onBlur,
  onFormat,
  onValidate,
  validationType,
  validationTooltipClassName,
  placeholder,
  ...props
}: IFormInputProps<T>) => {
  const { handleBlur, handleChange, value, validationMessage, isModified } = useFormField<T>({
    field,
    formKey,
    defaultValue,
    onChange,
    onValidate,
    isDisabled,
    isRequired,
    isReadOnly,
    isVisible,
    onBlur,
    validationType,
  })

  const t = useSafetyTranslation()
  const translatedPlaceholder = placeholder ? t(placeholder) : undefined

  const processingChangeEvent = useCallback(
    async (value: string) => {
      if (onFormat) {
        const formattedValue = onFormat(value as T)
        await handleChange(formattedValue)
        return
      }

      await handleChange(value as T)
    },
    [handleChange, onFormat]
  )

  if (isVisible) {
    if (isReadOnly) {
      return <ReadOnlyField label={label} value={value} />
    }

    return (
      <div className={styles.validationContainer}>
        <Tooltip
          disabled={!validationMessage}
          tipContent={() => <Translation text={validationMessage} htmlAvailable span />}
          viewClassName={validationTooltipClassName}
        >
          <Input
            classes={{
              root: cn(className, {
                [styles.required]: isRequired && Boolean(label),
                [styles.modified]: isModified,
                [styles.invalid]: validationMessage,
              }),
              rightSide: endContentClassName,
            }}
            label={label && <Translation text={label} span />}
            value={value === null ? '' : value}
            onBlur={handleBlur}
            onChange={processingChangeEvent}
            disabled={isDisabled || props.disabled}
            placeholder={translatedPlaceholder}
            {...props}
          />
        </Tooltip>
      </div>
    )
  }

  return null
}

export const FormInput = memo(CustomInput) as typeof CustomInput
